import { PartialMessage } from '@bufbuild/protobuf'
import { StoreProduct } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_product_pb'
import { StoreProductVariant } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_product_variant_pb'
import { CheckFilledIcon, CirclePlaceholderOnIcon } from '@fingertip/icons'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { getSingleProductPrice } from './product-price'

type Props = {
  variants: PartialMessage<StoreProductVariant>[]
  selectedVariant: PartialMessage<StoreProductVariant> | null
  onSelect: (variant: PartialMessage<StoreProductVariant>) => void
  product: PartialMessage<StoreProduct>
}

export const ProductVariantSelector = ({
  variants,
  selectedVariant,
  onSelect,
  product,
}: Props) => {
  const { t } = useTranslation()

  return (
    <div className="mb-4">
      <div className="mb-2">
        <Label>{t('select_option')}</Label>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {(variants || []).map((variant) => {
          const { buttonText } = getSingleProductPrice({
            priceInCents: variant?.priceInCents || 0,
            salePriceInCents: variant?.salePriceInCents || 0,
            hasSale: variant?.sale,
            currency: product?.currency || 'usd',
            t,
          })

          return (
            <Button
              key={variant.id}
              className="h-auto! flex-col text-left whitespace-normal"
              variant={
                selectedVariant?.id === variant.id ? 'default' : 'secondary'
              }
              onClick={() => onSelect(variant)}
            >
              <input
                type="radio"
                name="variant"
                checked={selectedVariant?.id === variant.id}
                onChange={() => onSelect(variant)}
                className="hidden"
              />

              <div className="flex w-full items-center space-x-2">
                {selectedVariant?.id === variant.id ? (
                  <CheckFilledIcon className="size-6 shrink-0" />
                ) : (
                  <CirclePlaceholderOnIcon className="size-6 shrink-0" />
                )}

                <div
                  className={cn('w-full flex-1', {
                    'line-through': variant?.isSoldOut,
                  })}
                >
                  {variant.name}
                </div>

                <div>{buttonText}</div>
              </div>

              {variant?.description && (
                <div
                  className="prose prose-sm w-full text-current prose-headings:text-current prose-p:text-current prose-a:text-current prose-a:underline prose-strong:text-current prose-em:text-current"
                  dangerouslySetInnerHTML={{ __html: variant.description }}
                />
              )}
            </Button>
          )
        })}
      </div>
    </div>
  )
}
